import React, {
  useState, useCallback, useEffect, forwardRef, memo,
} from 'react';
import useLocale from '@/hooks/useLocale';
import * as Styles from './SSHAccess.styles';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import useServerStatus from '@/hooks/useServerStatus';
import { VpsOciStatusEnum } from '@/pages/Products/products.enum';
import {
  Button, CopySnippet, Tooltip, IconInfo,
} from 'gatorcomponents';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ENABLE_COPY_SNIPPETS_ACCESS_SSH_VPS_PAGE } from '@/config/GrowthBook/constants';

import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

import ContentSection from '@/pages/Products/VpsOciDetailPage/commons/ContentSection';
import ChangePasswordModal from '@/pages/Products/VpsOciDetailPage/components/ChangePasswordModal';

const SSHAccess = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const {
    ssh,
    passwordLoading,
    passwordSuccess,
  } = useSelector(state => ({
    ssh: state.vpsOci?.data?.ssh,
    passwordLoading: state.vpsOci?.password?.loading,
    passwordSuccess: state.vpsOci?.password?.success,
  }), shallowEqual);

  const enabledCopySnippets = useFeatureIsOn(ENABLE_COPY_SNIPPETS_ACCESS_SSH_VPS_PAGE);

  const hasPassword = ssh && typeof ssh.hasPassword !== 'undefined' ? ssh.hasPassword : true;
  const [newInstallation, setNewInstallation] = useState(!hasPassword);

  const { serverInfo, packageId } = useSelector(state => ({
    serverInfo: state.vpsOci?.data?.serverInfo,
    packageId: state.vpsOci?.data?.packageId,
  }), shallowEqual);

  const { id: hostingId } = useParams();

  const {
    serverStatus,
  } = useServerStatus({
    serverInfo,
    serverId: hostingId,
    serverPackageId: packageId,
  });
  const isVpsOciOnline = serverStatus === VpsOciStatusEnum.ONLINE;

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(!hasPassword);
  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: localeVpsOci } = productDetailsLocale;

  const handleChangePassword = useCallback(() => {
    setShowChangePasswordModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setNewInstallation(false);
    setShowChangePasswordModal(false);
  }, []);

  const handleConfirmPassword = useCallback((password, confirmPassword) => {
    if (hostingId) {
      dispatch(vpsOciActions.saveSshPassword.request(hostingId, password, confirmPassword));
    }
  }, [hostingId, dispatch]);

  useEffect(() => {
    if (!passwordLoading && passwordSuccess && showChangePasswordModal) {
      setNewInstallation(false);
      setShowChangePasswordModal(false);
      dispatch(vpsOciActions.saveSshPassword.success(false));
    }
  }, [passwordLoading, passwordSuccess, showChangePasswordModal, dispatch]);

  if (!ssh) return null;

  const fields = [
    {
      name: 'ip',
      label: localeVpsOci.infoSsh.serverIp,
      value: ssh.serverIp,
    },
    {
      name: 'port',
      label: (
        <Tooltip
          content={localeVpsOci.infoSsh.serverPortTooltip}
          preferredPosition="right"
        >
          <Styles.PortLabelContent>
            <Styles.PortLabel>{localeVpsOci.infoSsh.serverPort}</Styles.PortLabel>
            <IconInfo size="sm2" color="lowDark" />
          </Styles.PortLabelContent>
        </Tooltip>
      ),
      value: ssh.serverPort,
    },
    {
      name: 'user',
      label: localeVpsOci.infoSsh.user,
      value: ssh.user,
    },
  ].filter(item => item.value);

  if (!fields || fields.length === 0) return null;

  return (
    <ContentSection
      ref={ref}
      direction="column"
      align="flex-start"
      justify="flex-start"
      flex={1}
      title={localeVpsOci.infoSsh.title.text}
      tooltip={localeVpsOci.infoSsh.title.tooltip}
      testId="ssh-info-section"
    >
      <Styles.Content>
        <Styles.FieldsList>
          {fields.map(field => (
            <Styles.FieldItem key={field.name} data-testid={`ssh-field-${field.name}`}>
              <Styles.FieldLabel>{field.label}</Styles.FieldLabel>
              <Styles.FieldWrapper>
                {enabledCopySnippets ? (
                  <CopySnippet
                    content={field.value}
                    withBorder={false}
                    width="fit"
                    withPadding={false}
                    iconSize="sm2"
                    testId={`ssh-copy-${field.name}`}
                  />
                ) : (
                  <Styles.FieldValue data-testid={`ssh-value-${field.name}`}>
                    {field.value}
                  </Styles.FieldValue>
                )}
              </Styles.FieldWrapper>
            </Styles.FieldItem>
          ))}
          {isVpsOciOnline && (
            <Styles.FieldItem data-testid="ssh-field-password">
              <Styles.FieldLabel>{localeVpsOci.infoSsh.password}</Styles.FieldLabel>
              <Styles.PasswordWrapper>
                <Button
                  variant="text"
                  label={localeVpsOci.infoSsh.changePassword}
                  onClick={handleChangePassword}
                  testId="ssh-change-password-link"
                  size="large"
                  version="v2"
                />
              </Styles.PasswordWrapper>
            </Styles.FieldItem>
          )}
        </Styles.FieldsList>

      </Styles.Content>
      {(showChangePasswordModal && hostingId) && (
        <ChangePasswordModal
          onClose={handleCloseModal}
          onConfirm={handleConfirmPassword}
          newInstallation={newInstallation}
          isLoading={passwordLoading}
        />
      )}
    </ContentSection>
  );
});

SSHAccess.displayName = 'SSHAccess';

export default memo(SSHAccess);
